import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, CircularProgress, Button, useTheme, useMediaQuery } from '@mui/material';
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, Legend, Line, ReferenceLine, CartesianGrid, LabelList, ResponsiveContainer } from 'recharts';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';
import DateRangePicker from './dashboard/common/DateRangePicker';
import SummaryCard from './dashboard/common/SummaryCard';
import { CUSTOMER_COMPANY_MAP, CUSTOMER_INFO_MAP, resolveCustomerIdFromUserDoc, type CustomerId } from '../config/customerConfig';
import { getCustomerCache, setCustomerCache } from '../utils/customerCache';

// 화주 정보 매핑을 위한 타입 (기존 타입과 호환성 유지)
interface LegacyCustomerInfo {
  name: string; 
  color: string;
  backgroundColor: string;
  lightColor: string;
}

// config의 CustomerInfo를 기존 타입으로 변환하는 함수
const convertToLegacyFormat = (customerId: CustomerId): LegacyCustomerInfo => {
  const info = CUSTOMER_INFO_MAP[customerId];
  return {
    name: info.name,
    color: info.color,
    backgroundColor: info.backgroundColor,
    lightColor: info.lightColor
  };
};

// 화주용 데이터 타입 정의 (totalBilling만 포함)
interface CustomerMonthlyData {
  month: string;
  totalBilling: number;
}

// 화주별 금액 포맷팅 (화주에 따라 다른 단위 사용)
const formatCurrency = (value: number): string => {
  if (value >= 100000000) {
    const eok = Math.floor(value / 100000000);
    const remainder = Math.floor((value % 100000000) / 10000000);
    if (remainder > 0) {
      return `${eok}억${remainder}천`;
    } else {
      return `${eok}억`;
    }
  } else if (value >= 10000000) {
    const thousand = Math.floor(value / 10000000);
    const remainder = Math.floor((value % 10000000) / 1000000);
    if (remainder > 0) {
      return `${thousand}천${remainder}백`;
    } else {
      return `${thousand}천`;
    }
  } else if (value >= 1000000) {
    const hundred = Math.floor(value / 1000000);
    const remainder = Math.floor((value % 1000000) / 100000);
    if (remainder > 0) {
      return `${hundred}백${remainder}십`;
    } else {
      return `${hundred}백`;
    }
  } else if (value >= 10000) {
    return `${Math.floor(value / 10000)}만`;
  } else if (value >= 1000) {
    return `${Math.floor(value / 1000)}천`;
  } else {
    return `${value}원`;
  }
};

// 다크 테마 스타일
const getDarkStyles = (customerColor: string) => ({
  paper: {
    bgcolor: '#1a1a1a',
    color: '#fff',
    p: { xs: 2, sm: 3 },
    mb: 2,
    borderRadius: 2,
    border: `1px solid ${customerColor}40`
  },
  title: {
    color: customerColor,
    fontWeight: 700,
    mb: 2,
    fontSize: { xs: 18, sm: 20 }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: { xs: 200, sm: 300 }
  },
  chartContainer: {
    height: { xs: 250, sm: 350 },
    width: '100%'
  }
});

interface Props {
  onNavigateToMain?: () => void;
  showStatsTab?: boolean;
}

const CustomerStatsPage: React.FC<Props> = ({ onNavigateToMain, showStatsTab }) => {
  // Hook은 항상 최상단에서 호출
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const exportRef = useRef<HTMLDivElement>(null);

  // 사용자 및 화주 정보
  const [user, setUser] = useState<User | null>(null);
  const [customerInfo, setCustomerInfo] = useState<LegacyCustomerInfo | null>(null);
  const [resolvedCustomerId, setResolvedCustomerId] = useState<CustomerId | null>(null);

  // 날짜 범위 상태
  const [dateRange, setDateRange] = useState({
    start: dayjs().subtract(11, 'month').startOf('month'),
    end: dayjs().endOf('month')
  });

  // 데이터 상태
  const [monthlyData, setMonthlyData] = useState<CustomerMonthlyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 화주 정보 초기화
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser?.email) {
        const userSnap = await getDoc(doc(db, 'users', currentUser.email));
        const cidField = userSnap.exists() ? userSnap.data()?.customerId : undefined;
        const customerId = resolveCustomerIdFromUserDoc(currentUser.email, cidField);
        setResolvedCustomerId(customerId);
        setCustomerInfo(convertToLegacyFormat(customerId));
      } else {
        setResolvedCustomerId(null);
      }
    });
    return unsubscribe;
  }, []);

  // 화주용: 월별 운송료 데이터 가져오기
  const fetchMonthlyData = async () => {
    if (!user?.email || !resolvedCustomerId) return;
    setLoading(true);
    setError(null);
    try {
      const companyName = CUSTOMER_COMPANY_MAP[resolvedCustomerId];
      if (!companyName) {
        throw new Error('화주 정보를 찾을 수 없습니다');
      }
      // const cacheKey = `customer-stats-${companyName}`;
      // const ttl = 24 * 60 * 60 * 1000; // 1일
      // const cached = getCustomerCache<CustomerMonthlyData[]>(cacheKey, ttl);
      // if (cached) {
      //   setMonthlyData(cached);
      //   setLoading(false);
      //   return;
      // }
      const response = await fetch(`/api/bigquery-dashboard/customer/monthly-billing/${encodeURIComponent(companyName)}`);
      if (!response.ok) throw new Error('데이터를 불러오지 못했습니다');
      const data = await response.json();
      setMonthlyData(data);
      // setCustomerCache(cacheKey, data, ttl);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 데이터 로드 (사용자 정보가 있을 때만)
  useEffect(() => {
    if (user?.email && resolvedCustomerId) {
      fetchMonthlyData();
    }
  }, [user?.email, resolvedCustomerId]);

  // 날짜 변경 핸들러
  const handleDateChange = (type: 'start' | 'end', value: Dayjs | null) => {
    if (!value) return;
    setDateRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // 기간 필터링된 데이터
  const filteredMonthlyData = monthlyData.filter(row => 
    row.month >= dateRange.start.format('YYYY-MM') && 
    row.month <= dateRange.end.format('YYYY-MM')
  );

  // 전년 동월 대비 증감률 계산
  const filteredMonthlyDataWithDiff = filteredMonthlyData.map(row => {
    const prevYearMonth = dayjs(row.month + '-01').subtract(1, 'year').format('YYYY-MM');
    const prev = monthlyData.find(r => r.month === prevYearMonth);
    if (!prev || prev.totalBilling === 0) {
      return { ...row, prevYearDiffRate: null };
    }
    const diff = row.totalBilling - prev.totalBilling;
    const diffRate = (diff / prev.totalBilling) * 100;
    return { ...row, prevYearDiffRate: diffRate };
  });

  // 평균 운송료 계산
  const avgTotalBilling = filteredMonthlyDataWithDiff.length > 0
    ? filteredMonthlyDataWithDiff.reduce((sum, row) => sum + row.totalBilling, 0) / filteredMonthlyDataWithDiff.length
    : 0;

  // 조건부 렌더링은 Hook 호출 이후에!
  if (showStatsTab === false) return null;

  if (!customerInfo) return <CircularProgress />;

  const darkStyles = getDarkStyles(customerInfo.color);

  return (
    <Box sx={{ 
      bgcolor: '#0a0a0a',
      color: '#fff',
      p: { xs: 1, sm: 2 },
      pb: { xs: 10, sm: 3 } // 모바일에서 하단 네비게이션 바 높이만큼 패딩 추가
    }}>
      {/* 상단 헤더 */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'flex-end',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        {onNavigateToMain && (
          <Button
            variant="outlined"
            onClick={onNavigateToMain}
            sx={{
              borderColor: customerInfo.color,
              color: customerInfo.color,
              fontSize: { xs: 12, sm: 14 },
              '&:hover': {
                borderColor: customerInfo.color,
                bgcolor: `${customerInfo.color}20`
              }
            }}
          >
            ← 메인으로
          </Button>
        )}
      </Box>

      {/* 날짜 선택 및 요약 정보 */}
      <Paper elevation={0} sx={darkStyles.paper}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap'
        }}>
          <DateRangePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            onStartDateChange={(date) => handleDateChange('start', date)}
            onEndDateChange={(date) => handleDateChange('end', date)}
          />

          {/* 요약 카드 */}
          {filteredMonthlyData.length > 0 && (
            <Box sx={{ 
              display: 'flex',
              gap: 1,
              ml: { xs: 0, sm: 'auto' },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <SummaryCard
                title="총 운송료"
                value={filteredMonthlyData.reduce((sum, item) => sum + item.totalBilling, 0)}
                color={customerInfo.color}
              />
              <SummaryCard
                title="평균 운송료"
                value={avgTotalBilling}
                color="#ffc107"
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* 차트/표 전체 영역 */}
      <div ref={exportRef}>
        {/* 월별 운송료 차트 */}
        <Paper elevation={0} sx={darkStyles.paper}>
          <Typography variant="h6" sx={darkStyles.title}>
            월별 운송료 추이
          </Typography>

          {loading ? (
            <Box sx={darkStyles.loadingContainer}>
              <CircularProgress size={36} />
            </Box>
          ) : error ? (
            <Box sx={{ color: 'error.main', p: 2 }}>{error}</Box>
          ) : filteredMonthlyData.length > 0 ? (
            <Box sx={darkStyles.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={filteredMonthlyDataWithDiff}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#888', fontSize: isMobile ? 10 : 12 }} 
                  />
                  <YAxis 
                    tick={{ fill: '#888', fontSize: isMobile ? 10 : 12 }}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const billing = payload.find(p => p.dataKey === 'totalBilling');
                      return (
                        <div style={{ 
                          background: '#2a2a2a', 
                          border: `1px solid ${customerInfo.color}`, 
                          color: '#fff', 
                          padding: 10, 
                          borderRadius: 6,
                          fontSize: isMobile ? 12 : 14
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                          {billing && (
                            <div style={{ color: customerInfo.color, fontWeight: 700 }}>
                              운송료: {formatCurrency(typeof billing.value === 'number' ? billing.value : 0)}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    iconType="circle"
                    wrapperStyle={{ color: '#888', fontSize: isMobile ? 12 : 14 }}
                  />
                  <Bar 
                    dataKey="totalBilling" 
                    name="운송료" 
                    fill={customerInfo.color}
                    radius={[4, 4, 0, 0]}
                  >
                    {/* PC에서만 금액 표시, 모바일에서는 숨김 */}
                    {!isMobile && (
                      <LabelList
                        dataKey="totalBilling"
                        position="top"
                        formatter={(value: number) => formatCurrency(value)}
                        style={{ 
                          fontSize: 14, 
                          fill: customerInfo.color, 
                          fontWeight: 700, 
                          textShadow: '0 0 2px #000' 
                        }}
                      />
                    )}
                    {/* PC에서만 증감률 표시, 모바일에서는 숨김 */}
                    {!isMobile && (
                      <LabelList
                        dataKey="prevYearDiffRate"
                        position="top"
                        content={({ x, y, value, width }) => {
                          if (typeof value !== 'number') return null;
                          const isUp = value > 0;
                          const color = isUp ? '#f44336' : '#2196f3'; // 상승: 빨간색, 하락: 파란색
                          const yPos = typeof y === 'number' ? y - 36 : -36;
                          const xPos = typeof x === 'number' && typeof width === 'number' ? x + width / 2 : x;
                          return (
                            <text
                              x={xPos}
                              y={yPos}
                              fill={color}
                              fontWeight="900"
                              fontSize={16}
                              textAnchor="middle"
                            >
                              {isUp ? '▲' : '▼'}{Math.abs(value).toFixed(1)}%
                            </text>
                          );
                        }}
                      />
                    )}
                  </Bar>
                  <Line
                    type="monotone"
                    dataKey="totalBilling"
                    name="추세선"
                    stroke="#2196f3"
                    strokeWidth={2}
                    dot={{ fill: '#2196f3', r: isMobile ? 2 : 4 }}
                  />
                  <ReferenceLine
                    y={avgTotalBilling}
                    stroke="#00bcd4"
                    strokeDasharray="6 3"
                    label={({ viewBox }) => {
                      const { width, y } = viewBox;
                      return (
                        <text
                          x={width - 10}
                          y={y - 8}
                          fill="#00bcd4"
                          fontWeight={700}
                          fontSize={isMobile ? 12 : 15}
                          textAnchor="end"
                        >
                          {`평균: ${formatCurrency(avgTotalBilling)}`}
                        </text>
                      );
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box sx={darkStyles.loadingContainer}>
              데이터가 없습니다.
            </Box>
          )}
        </Paper>

        {/* 월별 운송료 표 - 사내 대시보드 스타일 적용 */}
        <TableContainer component={Paper} sx={{ 
          mt: 1, 
          bgcolor: '#222',
          borderRadius: 1,
          border: '1px solid #444'
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  borderBottom: '1px solid #444'
                }}>날짜</TableCell>
                <TableCell sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  borderBottom: '1px solid #444'
                }}>운송료</TableCell>
                <TableCell sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  borderBottom: '1px solid #444'
                }}>전년 동월 대비</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMonthlyDataWithDiff.map((row, idx) => (
                <TableRow key={row.month || idx}>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 500,
                    borderBottom: '1px solid #444'
                  }}>{row.month}</TableCell>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 500,
                    borderBottom: '1px solid #444'
                  }}>{formatCurrency(row.totalBilling)}</TableCell>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 500,
                    borderBottom: '1px solid #444'
                  }}>
                    {row.prevYearDiffRate !== null ? (
                      <span style={{ 
                        color: row.prevYearDiffRate > 0 ? '#f44336' : '#2196f3', // 상승: 빨간색, 하락: 파란색
                        fontWeight: 700 
                      }}>
                        {row.prevYearDiffRate > 0 ? '▲' : '▼'}{Math.abs(row.prevYearDiffRate).toFixed(1)}%
                      </span>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
              
              {/* 합계 행 */}
              <TableRow>
                <TableCell sx={{
                  color: '#fff',
                  fontWeight: 700,
                  borderBottom: '1px solid #444'
                }}>합계</TableCell>
                <TableCell sx={{
                  color: '#fff',
                  fontWeight: 700,
                  borderBottom: '1px solid #444'
                }}>
                  {formatCurrency(filteredMonthlyDataWithDiff.reduce((sum, row) => sum + row.totalBilling, 0))}
                </TableCell>
                <TableCell sx={{
                  color: '#fff',
                  fontWeight: 700,
                  borderBottom: '1px solid #444'
                }}>-</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>

    </Box>
  );
};

export default CustomerStatsPage; 